import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  BillingCycle,
  BillingView,
  DowngradeChoices,
  DowngradePreview,
  OrderView,
  PlanCode,
  ScanPaymentProvider,
} from '@icetus/contracts';
import { PAYMENT_PROVIDER_LABEL } from '@icetus/contracts';
import { Badge, Button, Card, formatMoney } from '@icetus/ui';
import {
  ApiError,
  cancelBillingOrder,
  changePlan,
  createBillingOrder,
  fetchBilling,
  fetchBillingOrder,
  fetchDowngradePreview,
  mockPayBillingOrder,
} from '../api';
import { useConfirm } from '../components/confirm';
import { DowngradeDialog } from '../components/downgrade-dialog';
import { planLabel } from '../lib/plan-label';

/**
 * 套餐与账单。
 *
 * 这个页面承担一件很具体的事：**让客户在试用到期前把钱付了**。
 * 所以顺序是「我现在什么状态、还剩几天」→「点这里付」→「付完立刻恢复」，
 * 而不是先摆一张三档对比表让客户自己研究。
 *
 * 三条实现上的取舍：
 *   - 金额一律用服务端返回的分，前端不自己乘除。升级补差是按剩余天数算的，
 *     前端算一遍必然和服务端差几毛，客户会截图来问
 *   - 二维码用服务端生成的 SVG 内联渲染，不引第三方二维码服务 ——
 *     支付链接里带商户单号，发给外部服务等于把单号摊出去
 *   - 付完不是"前端说我付了"，而是每 2 秒问一次服务端；服务端再去微信查单
 */

const CYCLE_LABEL: Record<BillingCycle, string> = { monthly: '月付', annual: '年付' };
const KIND_LABEL: Record<string, string> = {
  new: '新购',
  renew: '续费',
  upgrade: '升档补差',
};
const STATUS_LABEL: Record<string, string> = {
  trialing: '试用中',
  active: '使用中',
  past_due: '待续费',
  suspended: '已暂停',
  canceled: '已取消',
  terminated: '已终止',
};

function money(cents: number): string {
  // 人民币用 zh-CN 格式化：`¥129.00` 比 `CN¥129.00` 更像国内报价
  return formatMoney(cents, 'CNY', 'zh-CN');
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function BillingPage({ tenantId }: { tenantId: string }) {
  const [view, setView] = useState<BillingView | null>(null);
  const [cycle, setCycle] = useState<BillingCycle>('monthly');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [order, setOrder] = useState<OrderView | null>(null);
  /**
   * 客户选的付款方式。
   *
   * 只有两个通道都开着时才让他选 —— 只有一个时选是假选择：唯一那个按钮
   * 旁边还摆一个灰的，客户会去点灰的然后以为坏了。
   */
  const [payProvider, setPayProvider] = useState<ScanPaymentProvider>('wechat');
  /**
   * 降档的影响清单。
   *
   * 有值就说明弹窗开着 —— 打开前先问服务端"会失去什么"，因为那需要站点列表、
   * 每站语言与页面数、成员角色，前端拼不出来，也不该拼。
   */
  const [downgrade, setDowngrade] = useState<DowngradePreview | null>(null);
  // 关单不需要二次确认：它只是取消一张没付钱的单，随时可以重新下单
  const { confirmDialog } = useConfirm();
  const pollTimer = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      const next = await fetchBilling(tenantId);
      setView(next);
      setOrder(next.pendingOrder);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  // 有待支付单就每 2 秒问一次。付完立刻停，不留一个永久轮询在后台
  useEffect(() => {
    if (!order || order.status !== 'pending') {
      if (pollTimer.current !== null) {
        window.clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
      return;
    }
    pollTimer.current = window.setInterval(() => {
      void fetchBillingOrder(tenantId, order.id)
        .then((next) => {
          setOrder(next);
          if (next.status === 'paid') {
            setNotice('已到账，站点已恢复。');
            void load();
          }
        })
        .catch(() => {
          // 轮询失败不打扰客户：二维码还在，下一轮会再试
        });
    }, 2000);
    return () => {
      if (pollTimer.current !== null) {
        window.clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
    };
  }, [order, tenantId, load]);

  const act = async (key: string, fn: () => Promise<void>): Promise<void> => {
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  if (error && !view) {
    return (
      <Card title="读不到账单" description={error}>
        <Button onClick={() => void load()}>重试</Button>
      </Card>
    );
  }

  if (!view) return <Card title="正在读取套餐…" description="稍等一下" />;

  const sub = view.subscription;
  const urgent = sub.daysLeft !== null && sub.daysLeft <= 3 && sub.status !== 'active';
  const stopped = !sub.siteVisible;
  /**
   * 两个扫码通道都可用时才让客户选；只有一个可用时 `{}` —— 不传 provider，
   * 由服务端挑它有的那一个。前端在这里传一个"猜的"值，等于替服务端决定
   * 一件它更清楚的事。
   */
  const bothScan = view.wechatEnabled && view.alipayEnabled;
  const providerArg = bothScan ? { provider: payProvider } : {};

  /** 提交降档方案：带着客户逐项勾好的选择一起写库 */
  const submitDowngrade = (choices: DowngradeChoices): void => {
    const target = downgrade;
    if (!target) return;
    void act('downgrade', async () => {
      await changePlan(tenantId, target.to.code, choices);
      setDowngrade(null);
      setNotice(
        `已登记：本周期结束后降为${target.to.nameZh}，到期那天按你刚才选的方案执行，之后按新价格续费。`,
      );
      await load();
    });
  };

  /**
   * 「算了，我都想保留，按原套餐续费」。
   *
   * 两步都不能少：只撤销不付款的话，到期一样停站，而客户以为他已经"选好了"
   * —— 那比什么都不做更糟。所以取消降档之后直接按当前档下续费单。
   */
  const keepCurrentPlan = (): void => {
    void act('keep', async () => {
      await changePlan(tenantId, null);
      const next = await createBillingOrder(tenantId, {
        planCode: sub.planCode,
        cycle,
        ...providerArg,
      });
      setDowngrade(null);
      setOrder(next);
      setNotice(
        next.status === 'paid'
          ? '已按原套餐续费。'
          : '降档已取消，已按原套餐下单：扫码付款后按当前套餐继续。',
      );
      await load();
    });
  };

  return (
    <>
      {confirmDialog}

      <StockState view={view} urgent={urgent} stopped={stopped} />

      {notice ? <div className="notice notice--ok">{notice}</div> : null}
      {error ? <div className="notice notice--warn">{error}</div> : null}

      {order && order.status === 'pending' ? (
        <PendingOrder
          order={order}
          manualInstructions={view.manualInstructions}
          mockPayEnabled={view.mockPayEnabled}
          busy={busy === order.id}
          onCancel={() =>
            void act(order.id, async () => {
              await cancelBillingOrder(tenantId, order.id);
              setOrder(null);
              setNotice('订单已关闭。');
              await load();
            })
          }
          onMockPay={() =>
            void act(order.id, async () => {
              const settled = await mockPayBillingOrder(tenantId, order.id);
              setOrder(settled);
              setNotice('已按模拟支付处理：订单落账、订阅周期起算、站点恢复。');
              // 还要重读一次整页：套餐名、配额、到期日都变了，
              // 只更新这一张单会让下面的面板留着旧数字
              await load();
            })
          }
          switchTo={
            order.provider === 'wechat' && view.alipayEnabled
              ? 'alipay'
              : order.provider === 'alipay' && view.wechatEnabled
                ? 'wechat'
                : null
          }
          onSwitchProvider={(next) =>
            void act(order.id, async () => {
              /**
               * 换通道 = 重新下一单（服务端会关掉旧的那张）。
               *
               * 不是"把旧单改成支付宝"：单号已经报给过通道，金额、单号、
               * 二维码三者绑在一起，改一个就等于伪造一张对方不认识的单。
               */
              const created = await createBillingOrder(tenantId, {
                planCode: order.planCode,
                cycle: order.cycle,
                provider: next,
              });
              setOrder(created);
              setPayProvider(next);
              if (created.status === 'paid') {
                setNotice('已开通。');
                await load();
              }
            })
          }
        />
      ) : null}

      <Card
        title="选择套餐"
        description="月付随时可以停；年付按十个月的价格收，相当于省两个月。"
        actions={
          <div className="segmented">
            {(['monthly', 'annual'] as const).map((c) => (
              <button
                key={c}
                type="button"
                className={`segmented__item${cycle === c ? ' is-active' : ''}`}
                onClick={() => setCycle(c)}
              >
                {CYCLE_LABEL[c]}
                {c === 'annual' ? ' 省两个月' : ''}
              </button>
            ))}
          </div>
        }
      >
        <div className="plans-grid">
          {bothScan ? (
            <div className="pay-method">
              <span className="pay-method__label">付款方式</span>
              <div className="segmented">
                {(['wechat', 'alipay'] as const).map((p) => (
                  <button
                    key={p}
                    type="button"
                    className={`segmented__item${payProvider === p ? ' is-active' : ''}`}
                    onClick={() => setPayProvider(p)}
                  >
                    {PAYMENT_PROVIDER_LABEL[p]}
                  </button>
                ))}
              </div>
              <span className="muted">扫码付款，付完自动开通</span>
            </div>
          ) : null}
          {view.plans.map((plan) => {
            const price = cycle === 'annual' ? plan.annualCents : plan.monthlyCents;
            const isCurrent = plan.current;
            const currentIndex = view.plans.findIndex((p) => p.current);
            const index = view.plans.findIndex((p) => p.code === plan.code);
            const isDowngrade = index < currentIndex;
            const pendingThis = sub.pendingPlanCode === plan.code;

            return (
              <article key={plan.code} className={`plan-card${isCurrent ? ' is-current' : ''}`}>
                <header>
                  <h3>{plan.nameZh}</h3>
                  {isCurrent ? <Badge tone="success">当前</Badge> : null}
                  {pendingThis ? <Badge tone="warn">下周期生效</Badge> : null}
                </header>
                <p className="plan-card__price">
                  {money(price)}
                  <span> / {cycle === 'annual' ? '年' : '月'}</span>
                </p>
                {cycle === 'annual' ? (
                  <p className="muted plan-card__hint">
                    折合 {money(Math.round(plan.annualCents / 12))} / 月
                  </p>
                ) : null}
                <ul className="plan-card__list">
                  <li>{plan.quotas.products.toLocaleString('zh-CN')} 个商品</li>
                  <li>{(plan.quotas.storage_mb / 1024).toFixed(0)} GB 存储</li>
                  <li>{plan.quotas.locales} 种语言</li>
                  <li>{plan.quotas.seats} 个成员账号</li>
                  <li>{plan.quotas.sites} 个站点</li>
                </ul>

                {isCurrent ? (
                  <Button
                    variant="primary"
                    disabled={busy !== null}
                    onClick={() =>
                      void act(plan.code, async () => {
                        const next = await createBillingOrder(tenantId, {
                          planCode: plan.code,
                          cycle,
                          ...providerArg,
                        });
                        setOrder(next);
                        if (next.status === 'paid') {
                          setNotice('已开通。');
                          await load();
                        }
                      })
                    }
                  >
                    {busy === plan.code
                      ? '创建订单…'
                      : sub.status === 'trialing'
                        ? '立即付费，保留试用期内容'
                        : '续费一个周期'}
                  </Button>
                ) : isDowngrade ? (
                  <Button
                    disabled={busy !== null}
                    onClick={() =>
                      void act(plan.code, async () => {
                        // 撤销是"什么都不收回"，不需要清单
                        if (pendingThis) {
                          await changePlan(tenantId, null);
                          setNotice('已撤销降档申请，本周期结束后按当前套餐续费。');
                          await load();
                          return;
                        }
                        /**
                         * 降档不走"点一下登记"，而是先打开影响清单。
                         *
                         * 客户必须在**这一刻**看到要收回什么并逐项确认 ——
                         * 到期那天没人能再问他。没有选择，服务端也会直接 409。
                         */
                        const view = await fetchDowngradePreview(tenantId, plan.code as PlanCode);
                        setDowngrade(view);
                      })
                    }
                  >
                    {pendingThis ? '撤销降档' : '下周期降为这一档'}
                  </Button>
                ) : (
                  <Button
                    disabled={busy !== null}
                    onClick={() =>
                      void act(plan.code, async () => {
                        const next = await createBillingOrder(tenantId, {
                          planCode: plan.code,
                          cycle,
                          ...providerArg,
                        });
                        setOrder(next);
                        if (next.status === 'paid') {
                          setNotice('已升级，额度已生效。');
                          await load();
                        }
                      })
                    }
                  >
                    {busy === plan.code ? '创建订单…' : '升级到这一档（只补差价）'}
                  </Button>
                )}
              </article>
            );
          })}
        </div>
      </Card>

      <Card title="本周期用量" description="超了不会停站，只是不能再加；已经有的照常访问。">
        <Usage label="商品" used={view.usage.products} limit={view.usage.productsLimit} unit="个" hint="归档的不算" />
        <Usage label="存储" used={view.usage.storageMb} limit={view.usage.storageLimitMb} unit="MB" />
        <Usage label="站点" used={view.usage.sites} limit={view.usage.sitesLimit} unit="个" />
        <Usage label="成员" used={view.usage.seats} limit={view.usage.seatsLimit} unit="人" />
        <Usage label="语言" used={view.usage.locales} limit={view.usage.localesLimit} unit="种" />
      </Card>

      <Card title="订单与账单" description="每一笔收款都在这里，可对照微信账单或银行流水。">
        {view.orders.length === 0 ? (
          <p className="muted">还没有订单。选一个套餐就能开始。</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>单号</th>
                <th>套餐</th>
                <th>周期</th>
                <th>类型</th>
                <th>金额</th>
                <th>状态</th>
                <th>创建时间</th>
              </tr>
            </thead>
            <tbody>
              {view.orders.map((o) => (
                <tr key={o.id}>
                  <td>
                    <code>{o.number}</code>
                  </td>
                  <td>{planLabel(o.planCode)}</td>
                  <td>{CYCLE_LABEL[o.cycle]}</td>
                  <td>{KIND_LABEL[o.kind] ?? o.kind}</td>
                  <td>{money(o.amountCents)}</td>
                  <td>
                    <Badge
                      tone={
                        o.status === 'paid'
                          ? 'success'
                          : o.status === 'pending'
                            ? 'warn'
                            : 'neutral'
                      }
                    >
                      {o.status === 'paid'
                        ? '已支付'
                        : o.status === 'pending'
                          ? '待支付'
                          : o.status === 'canceled'
                            ? '已取消'
                            : o.status === 'expired'
                              ? '已超时'
                              : '已退款'}
                    </Badge>
                  </td>
                  <td>{fmtDate(o.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {downgrade ? (
        <DowngradeDialog
          // key 用目标套餐：换一个档位重新打开时，预选必须重算，不能沿用上一个档的选择
          key={downgrade.to.code}
          preview={downgrade}
          busy={busy !== null}
          onClose={() => setDowngrade(null)}
          onConfirm={submitDowngrade}
          onKeepCurrent={keepCurrentPlan}
        />
      ) : null}
    </>
  );
}

function StockState({
  view,
  urgent,
  stopped,
}: {
  view: BillingView;
  urgent: boolean;
  stopped: boolean;
}) {
  const sub = view.subscription;
  if (stopped) {
    return (
      <div className="notice notice--danger">
        <span>
          <strong>{sub.planName}</strong> 已到期，站点现在打不开。数据保留 30 天，
          续费后立刻恢复（域名、内容、商品都还在）。
        </span>
      </div>
    );
  }
  return (
    <Card title={`${sub.planName} · ${STATUS_LABEL[sub.status] ?? sub.status}`}>
      <div className="quota">
        <span>到期日</span>
        <strong>{fmtDate(sub.periodEnd)}</strong>
        {sub.daysLeft !== null ? (
          <Badge tone={urgent ? 'warn' : 'neutral'}>还剩 {sub.daysLeft} 天</Badge>
        ) : null}
      </div>
      {sub.status === 'trialing' ? (
        <p className="muted">
          试用期内可以随便改，到期后站点会暂停。付费后内容一个都不会丢。
        </p>
      ) : null}
      {sub.status === 'past_due' && sub.graceUntil ? (
        <p className="muted">
          上次扣款没成功，宽限期到 {fmtDate(sub.graceUntil)}。这期间站点仍然可访问。
        </p>
      ) : null}
      {sub.pendingPlanCode ? (
        <p className="muted">已申请降档，当前周期结束后生效。</p>
      ) : null}
    </Card>
  );
}

function PendingOrder({
  order,
  manualInstructions,
  mockPayEnabled,
  busy,
  onCancel,
  onMockPay,
  switchTo,
  onSwitchProvider,
}: {
  order: OrderView;
  manualInstructions: string | null;
  /** 服务端开了模拟支付才有。它只是"要不要显示按钮"，真正的门在后端 */
  mockPayEnabled: boolean;
  busy: boolean;
  onCancel: () => void;
  onMockPay: () => void;
  /**
   * 还能改用哪个通道。`null` 表示没有别的可选（只配了一个，或已是人工收款）。
   *
   * 有这张按钮是因为：客户打开付款页之前并不知道我们会给哪种码。
   * 手里只有支付宝却看到一张微信码，他会做的第一件事是去找"换一种方式"，
   * 找不到就退出去了 —— 而这一单本来是可以付成的。
   */
  switchTo: ScanPaymentProvider | null;
  onSwitchProvider: (next: ScanPaymentProvider) => void;
}) {
  return (
    <Card
      title="待支付"
      description={`单号 ${order.number} · ${money(order.amountCents)}`}
      actions={
        <Button variant="ghost" disabled={busy} onClick={onCancel}>
          关闭订单
        </Button>
      }
    >
      {order.qrSvg && order.provider !== 'manual' ? (
        <div className="pay-qr">
          {/* SVG 由服务端生成：二维码内容带商户单号，不经过任何第三方 */}
          <div dangerouslySetInnerHTML={{ __html: order.qrSvg }} />
          <div>
            <p>
              <strong>用{PAYMENT_PROVIDER_LABEL[order.provider]}扫一扫</strong>
              付款，付完这个页面会自动刷新。
            </p>
            <p className="muted">
              二维码两小时内有效。关掉页面也没关系，回到这里还能看到同一张码。
            </p>
            {switchTo ? (
              <p className="muted">
                手机里没有{PAYMENT_PROVIDER_LABEL[order.provider]}？
                <button
                  type="button"
                  className="linkish"
                  disabled={busy}
                  onClick={() => onSwitchProvider(switchTo)}
                >
                  改用{PAYMENT_PROVIDER_LABEL[switchTo]}付款
                </button>
              </p>
            ) : null}
          </div>
        </div>
      ) : (
        <div>
          <p>
            这单走<strong>对公转账</strong>。转账时请在备注里写上单号{' '}
            <code>{order.number}</code>，我们确认后立刻开通。
          </p>
          {manualInstructions ? (
            <pre className="pay-manual">{manualInstructions}</pre>
          ) : (
            <p className="muted">
              收款信息请向对接的同事索取。开通后站点会自动恢复，不需要你再操作。
            </p>
          )}
        </div>
      )}

      {/**
       * 模拟支付：不扣款，直接按已付处理。
       *
       * 只对人工收款单显示 —— 有微信支付时该走真实扫码，不该给一条绕过的路。
       * 文案要写清"不产生扣款"，否则会把测试按钮当成付款入口。
       */}
      {mockPayEnabled && order.provider === 'manual' ? (
        <div className="pay-mock">
          <p>
            <strong>测试模式</strong>：下面这个按钮<strong>不产生任何扣款</strong>。
            它会走和真实支付完全相同的开通流程（落账、写账单、周期起算、站点恢复），
            用来在拿到支付资质之前先把这条链路验一遍。
          </p>
          <Button disabled={busy} onClick={onMockPay}>
            {busy ? '处理中…' : '模拟支付成功'}
          </Button>
        </div>
      ) : null}
    </Card>
  );
}

function Usage({
  label,
  used,
  limit,
  unit,
  hint,
}: {
  label: string;
  used: number;
  limit: number;
  unit: string;
  /** 补齐"这个数字怎么算的"，例如商品是"归档不算" */
  hint?: string;
}) {
  const ratio = limit > 0 ? used / limit : 0;
  const tone = ratio >= 1 ? 'danger' : ratio >= 0.8 ? 'warn' : 'neutral';
  return (
    <div className="quota">
      <span>
        {label}
        {hint ? <em className="quota__hint">{hint}</em> : null}
      </span>
      <strong>
        {used.toLocaleString('zh-CN')} / {limit.toLocaleString('zh-CN')} {unit}
      </strong>
      <Badge tone={tone}>{limit > 0 ? `${Math.round(ratio * 100)}%` : '无配额'}</Badge>
    </div>
  );
}
